import { useScaleGuestSiteFontSize } from '@shared/core/website/theming/useScaleGuestSiteFontSize';
import type { Category } from '@graphql/generated-base';
import type { TextV2Props } from '@withjoy/joykit';
import { TextV2 } from '@withjoy/joykit';
import { useIsMounted } from '@withjoy/joykit/hooks';
import { pxToRem } from '@withjoy/joykit/theme';
import React, { useEffect, useRef } from 'react';

interface ResponsiveText extends Omit<TextV2Props, 'fontSize'> {
  text: Maybe<string>;
  skipResize?: boolean;
  defaultFontSize?: number;
  fontCategory: Category;
}

export const ResponsiveText: React.FC<ResponsiveText> = ({ text, defaultFontSize = 48, skipResize, fontCategory, ...textV1Props }) => {
  const isMounted = useIsMounted();
  const ref = useRef<HTMLDivElement>(null);
  const lastMeasuredWidth = useRef(-1);
  const lastMeasuredViewportWidth = useRef(-1);
  const lastChosenFontSize = useRef<number | null>(null);

  const { scaleFontSize } = useScaleGuestSiteFontSize();

  useEffect(() => {
    // Any dependency change can alter the text's natural width, so the cached width from the
    // previous run must not suppress the next measurement.
    lastMeasuredWidth.current = -1;
    lastMeasuredViewportWidth.current = -1;
    lastChosenFontSize.current = null;

    const updateFontSize = () => {
      const element = ref.current;
      if (!element || !isMounted || skipResize) return;

      const fontSizeBeforeProbing = element.style.fontSize;
      // Read before any font-size write, while layout is still clean, so it forces no layout.
      const viewportWidth = window.innerWidth;
      const widthBeforeProbing = element.clientWidth;
      let widthAtLastProbe = widthBeforeProbing;

      // Each probe writes a font size and then reads scrollWidth, and that read forces a
      // synchronous layout of the whole page. Binary search keeps it to ~log2(defaultFontSize)
      // probes; stepping 1px at a time cost up to `defaultFontSize` of them per instance, per
      // resize — on the website designer's preview iframe that was 630 forced layouts for a
      // single panel open.
      //
      // clientWidth is re-read per probe rather than cached: a shrink-to-fit container's own
      // width moves with the font size, and layout is already flushed by the scrollWidth read,
      // so the second read costs nothing.
      const fitsAt = (fontSize: number) => {
        element.style.fontSize = scaleFontSize(`${pxToRem(fontSize)}`, fontCategory);
        const fits = element.scrollWidth <= element.clientWidth;
        widthAtLastProbe = element.clientWidth;
        return fits;
      };

      let low = 0;
      let high = defaultFontSize;
      let largestFitting: number | null = null;
      let widthAtLargestFitting = widthBeforeProbing;

      if (widthBeforeProbing === lastMeasuredWidth.current) {
        const lastChosen = lastChosenFontSize.current;
        if (lastChosen === null || lastChosen >= defaultFontSize) return;
        // A shrink-to-fit container keeps its width when the viewport grows, so an unchanged
        // width can still hide new room. One probe a size up tells the two cases apart — but only
        // a viewport-width change can add room, so height-only resizes (mobile URL bar) skip it.
        if (viewportWidth === lastMeasuredViewportWidth.current) return;
        lastMeasuredViewportWidth.current = viewportWidth;
        if (!fitsAt(lastChosen + 1)) {
          element.style.fontSize = fontSizeBeforeProbing;
          return;
        }
        // Fitting is monotonic in font size, so everything up to the probe is known to fit.
        largestFitting = lastChosen + 1;
        widthAtLargestFitting = widthAtLastProbe;
        low = lastChosen + 2;
      }

      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        if (fitsAt(mid)) {
          largestFitting = mid;
          widthAtLargestFitting = widthAtLastProbe;
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }

      // Nothing fits, not even at zero — the element is hidden or has no room at all. Keep the
      // size it came in with rather than collapsing the text to nothing.
      element.style.fontSize = largestFitting === null ? fontSizeBeforeProbing : scaleFontSize(`${pxToRem(largestFitting)}`, fontCategory);
      lastChosenFontSize.current = largestFitting;
      // The width read while probing the chosen size is the width the element settles at, so
      // the next resize compares against it without another forced layout.
      lastMeasuredWidth.current = widthAtLargestFitting;
      lastMeasuredViewportWidth.current = viewportWidth;
    };

    const timeout = setTimeout(() => updateFontSize(), 200);

    // Resize fires in bursts while a window is dragged or the designer's preview pane is
    // resized; measuring once per frame is enough and keeps the work off the resize handler.
    let frame: number | null = null;
    const handleResize = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updateFontSize);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (frame !== null) cancelAnimationFrame(frame);
      clearTimeout(timeout);
    };
  }, [text, isMounted, defaultFontSize, skipResize, scaleFontSize, fontCategory]);

  return (
    <TextV2
      ref={ref}
      {...textV1Props}
      {...(!skipResize && { whiteSpace: 'nowrap' })}
    >
      {text}
    </TextV2>
  );
};
